import type { ApiError, OpenapiError, UseQueryReturn } from '@core/types'
import type { components as ContentSchemas } from '@core/types/content-shapes'
import type { PressPost } from '@nimbus/types'
import { useContentConsent, useContentFaq, useContentGamePage, useContentGeneralConsent, useContentPage, useContentPress, useContentVendorList, useQuery } from '#imports'

/**
 * Jurisdiction-shared page data wrappers, re-exported by each layer's
 * app/composables/usePageData.ts. Lives outside core's registered composables
 * glob because it calls @spilnu/content auto-imports, which the playground
 * cannot resolve.
 *
 * All page-level data is served from Nuxt Content collections.
 */

function normalizeMedia(media?: { url: string, thumbnailUrl?: string }) {
    if (!media) return undefined
    return {
        url: media.url,
        formats: {
            thumbnail: {
                url: media.thumbnailUrl || media.url,
            },
        },
    }
}

export function usePageData(seoName: string | undefined) {
    const { getPageBySeoName } = useContentPage()

    return useQuery(async () => {
        if (!seoName) return null
        const page = await getPageBySeoName(seoName)
        return page as unknown as ContentSchemas['schemas']['Page']
    }, {
        key: () => `content:page-${seoName}`,
        enabled: () => !!seoName,
        lazy: false,
        server: true,
    })
}

type VendorList = Awaited<ReturnType<ReturnType<typeof useContentVendorList>['getVendorLists']>>[number] | null

/** The first vendor filter list, shared by the lobby pages that filter games by vendor. */
export function useVendorListData(): UseQueryReturn<VendorList, ApiError<VendorList> | OpenapiError<VendorList> | Error | null> {
    const { getVendorLists } = useContentVendorList()

    return useQuery(async () => (await getVendorLists())?.[0] ?? null, {
        key: 'content:vendor-list',
        server: true,
        lazy: false,
    })
}

export function useFaqData() {
    const { getFaqCollections } = useContentFaq()

    return useQuery(async () => {
        const collections = await getFaqCollections()
        const sections = collections?.[0]?.faqSections || []
        return (sections.length
            ? sections
            : null) as unknown as ContentSchemas['schemas']['FaqCollection']['faqSections'] | null
    }, {
        key: 'content:faq',
        lazy: false,
        server: true,
    })
}

export function useConsentData(slug: string) {
    const { getConsentBySlug } = useContentConsent()

    return useQuery(async () => {
        return await getConsentBySlug(slug)
    }, {
        key: () => `content:consent-${slug}`,
        lazy: false,
        server: true,
    })
}

export function useGeneralConsentData() {
    const { getGeneralConsents } = useContentGeneralConsent()

    return useQuery(async () => {
        const consents = await getGeneralConsents()
        return consents?.length ? consents[0] : null
    }, {
        key: 'content:general-consent',
        lazy: false,
        server: true,
    })
}

// TODO: Re-enable when blog content migration is ready
// export function useBlogData() { ... }
// export function useBlogPostData(slug: string) { ... }

export function usePressData() {
    const { getPressArticles } = useContentPress()

    return useQuery(async () => {
        const articles = await getPressArticles()
        if (!articles?.length) return null
        return articles.map(article => ({
            ...article,
            publishAt: article.publishAt
                ? { publishAt: article.publishAt }
                : undefined,
            media: normalizeMedia(article.media),
        })) as unknown as PressPost[]
    }, {
        key: 'content:press',
        lazy: false,
        server: true,
    })
}

export function useGamePageData(seoName: string) {
    const { getGamePageBySeoName } = useContentGamePage()

    return useQuery(async () => {
        const page = await getGamePageBySeoName(seoName)
        return page as unknown as ContentSchemas['schemas']['GamePage']
    }, {
        key: () => `content:game-page-${seoName}`,
        lazy: false,
        server: true,
    })
}

export function useGamePagesListData() {
    const { getGamePages } = useContentGamePage()

    return useQuery(async () => {
        const pages = await getGamePages()
        return pages as unknown as ContentSchemas['schemas']['GamePage'][]
    }, {
        key: 'content:game-pages',
        lazy: true,
        server: false,
    })
}

export function useGamePageOverviewData() {
    const { getGamePageOverview } = useContentGamePage()

    return useQuery(async () => {
        const overview = await getGamePageOverview()
        return overview as unknown as ContentSchemas['schemas']['GamePageOverview']
    }, {
        key: 'content:game-page-overview',
        lazy: false,
        server: true,
    })
}

export function usePressArticleData(slug: string) {
    const { getPressArticleBySlug } = useContentPress()

    return useQuery(async () => {
        const article = await getPressArticleBySlug(slug)
        if (!article) return null
        return {
            ...article,
            publishAt: article.publishAt
                ? { publishAt: article.publishAt }
                : undefined,
            media: normalizeMedia(article.media),
        } as unknown as ContentSchemas['schemas']['Press']
    }, {
        key: () => `content:press-article-${slug}`,
        lazy: false,
        server: true,
    })
}
